import { findOrderStatusConfig, ORDER_STATUS } from "../../constants/myAccount/orderStatus";
import { getOrderDetailsFromMyOrders } from "./orderHelpers";

/**
 * @param {object} myOrders
 * @param {string} subOrderId
 * @returns {object|null}
 */
export function findSubOrderRow(myOrders, subOrderId) {
  const orderDetails = getOrderDetailsFromMyOrders(myOrders);
  const list = orderDetails?.data?.subOrdersList ?? [];
  return list.find((row) => String(row?.subOrderDetails?.subOrderId) === String(subOrderId)) ?? null;
}

/**
 * @param {object} recipientDetails
 * @returns {object}
 */
export function mapRecipientDetailsToForm(recipientDetails) {
  if (!recipientDetails) {
    return {
      name: "",
      mobileNumber: "",
      altMobileNumber: "",
      email: "",
      address: "",
      localityArea: "",
      postalCode: "",
      city: "",
      countryGeoId: "IND",
      landmark: "",
      houseNo: "",
      flatHouseNo: "",
      directions: "",
      doorBell: "",
      addressType: "home",
      salutation: "",
    };
  }
  return {
    name: recipientDetails.recipientName ?? "",
    mobileNumber: recipientDetails.recipientContactNumber ?? "",
    altMobileNumber: recipientDetails.recipientAltContactNumber ?? "",
    email: recipientDetails.recipientEmail ?? "",
    address: recipientDetails.recipientAddress1 ?? recipientDetails.recipientAddress ?? "",
    localityArea: recipientDetails.recipientAddress2 ?? "",
    postalCode: recipientDetails.recipientPostalCode ?? "",
    city: recipientDetails.recipientCity ?? "",
    countryGeoId: recipientDetails.recipientCountryGeoId ?? "IND",
    landmark: recipientDetails.landmark ?? "",
    houseNo: recipientDetails.houseNo ?? "",
    flatHouseNo: recipientDetails.flatHouseNo ?? "",
    directions: recipientDetails.directions ?? "",
    doorBell: recipientDetails.doorBell ?? "",
    addressType: recipientDetails.addressType ?? "home",
    salutation: recipientDetails.salutation ?? "",
  };
}

/**
 * @param {object} form
 * @param {string} subOrderId
 * @returns {Record<string, string>}
 */
export function buildRecipientUpdatePayload(form, subOrderId) {
  return {
    sub_order_id: subOrderId,
    recipient_name: form.name,
    recipient_contact_number: form.mobileNumber,
    recipient_alt_contact_number: form.altMobileNumber ?? "",
    recipient_email: form.email ?? "",
    recipient_address1: form.address,
    recipient_address2: form.localityArea ?? "",
    recipient_pincode: form.postalCode,
    recipient_city: form.city,
    recipient_country_geo_id: form.countryGeoId ?? "IND",
    landmark: form.landmark ?? "",
    address_type: form.addressType ?? "home",
    house_no: form.houseNo ?? "",
    locality_area: form.localityArea ?? "",
    salutation: form.salutation ?? "",
    flat_house_no: form.flatHouseNo ?? "",
    directions: form.directions ?? "",
    door_bell: form.doorBell ?? "",
  };
}

/**
 * Cake and free message card stay editable only before Processing.
 * Processing, out for delivery, shipped, and delivered statuses are locked.
 * @param {string} subOrderStatus
 * @returns {boolean}
 */
export function isGiftMessageEditLockedByStatus(subOrderStatus) {
  const normalized = String(subOrderStatus ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  const statusConfig = findOrderStatusConfig(normalized);
  if (!statusConfig) {
    return false;
  }
  return statusConfig.index >= ORDER_STATUS.PROCESSING.index;
}

/**
 * @param {object} subOrderDetails
 * @returns {boolean}
 */
export function canEditOrderGiftMessages(subOrderDetails) {
  if (!subOrderDetails?.isOrderEditable) {
    return false;
  }
  return !isGiftMessageEditLockedByStatus(subOrderDetails?.subOrderStatus);
}

/**
 * @param {string} subOrderId
 * @param {string} productUniqueId
 * @returns {string}
 */
export function buildAccountMessageOnCardPath(subOrderId, productUniqueId) {
  return `/account/message-on-card/${subOrderId}/${productUniqueId}`;
}

/**
 * Cake save body for `/control/orderUpdateMessage`.
 * Keeps the existing card fields and replaces only `cake_message`, matching the legacy cake editor.
 * Undefined values are omitted so URLSearchParams does not send the string "undefined".
 * @param {object} product
 * @param {string} subOrderId
 * @param {string} cakeMessage
 * @returns {Record<string, string>}
 */
export function buildCakeMessageUpdatePayload(product, subOrderId, cakeMessage) {
  const messageOnCard = product?.messageOnCard ?? {};
  const fields = {
    productId: product?.productId,
    cake_message: cakeMessage,
    sub_order_id: subOrderId,
    order_item_sequence_id: product?.orderItemSeqId,
    occasion: messageOnCard.occasion,
    sender_name: messageOnCard.giftSenderName,
    recipient_name: messageOnCard.giftRecipientName,
    gift_message: messageOnCard.giftmessage ?? messageOnCard.msg,
  };

  return Object.fromEntries(
    Object.entries(fields)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, String(value)]),
  );
}
